import * as React from 'react';
import { clsx } from 'clsx';
import { NNBSP } from '../../../internal/constants/char';
import { Carousel } from '../../../internal/components/Carousel';
import { useDeviceType } from '../../../responsive';
import { Tag } from '../../internal/components/Tag';
import { useProvideCloudDisplayModeConfig, useProvideCloudDisplayModeRefs } from '../../internal/hooks';
import { ActionLink } from '../../../link';
import { cloudDisplayModeDefaultOptions } from './constants';
import styles from './styles.module.css';

/**
 * [Ссылка на макет](https://www.figma.com/design/zgc402IaHF0G4PW81es4bT/%F0%9F%8C%90-Tags)
 */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const Tags = props => {
  const {
    onClose,
    options,
    displayMode = 'cloud',
    displayModeOptions = cloudDisplayModeDefaultOptions,
    leftAdornment,
    rightAdornment
  } = props;
  const deviceType = useDeviceType();
  const isCloudMode = displayMode === 'cloud';
  const isPhoneDeviceType = deviceType === 'phone';
  const {
    showMoreText,
    hideText,
    isFoldable,
    expandCollapseAdornment
  } = displayModeOptions;
  const {
    wrapperRef,
    moreButtonRef,
    lessButtonRef,
    leftAdornmentRef,
    rightAdornmentRef
  } = useProvideCloudDisplayModeRefs();
  const {
    isFolded,
    cloudModeControlHandler,
    setTagWidth,
    clearTagWidth,
    numberOfTagsToDisplay,
    numberOfTagsHidden,
    isTagsFitMaxLines
  } = useProvideCloudDisplayModeConfig({
    tagsOptions: options,
    displayModeOptions,
    isCloudDisplayMode: isCloudMode,
    wrapperRef,
    moreButtonRef,
    lessButtonRef,
    leftAdornmentRef,
    rightAdornmentRef
  });
  const handleClose = React.useCallback(option => {
    onClose === null || onClose === void 0 || onClose(option);
    clearTagWidth(option.value);
  }, [onClose, clearTagWidth]);
  const cloudModeControls = React.useMemo(() => {
    if (!isCloudMode || !isFoldable || !numberOfTagsToDisplay) {
      return null;
    }
    if (isFolded && !numberOfTagsHidden) {
      return null;
    }
    if (isTagsFitMaxLines) {
      return null;
    }
    const customButton = expandCollapseAdornment === null || expandCollapseAdornment === void 0 ? void 0 : expandCollapseAdornment(!isFolded);
    if (customButton) {
      return /*#__PURE__*/_jsx("div", {
        className: styles['cloud_mode_controls'],
        ref: isFolded ? moreButtonRef : lessButtonRef,
        onClick: cloudModeControlHandler,
        children: customButton
      });
    }
    const buttonText = (() => {
      if (isFolded) {
        return showMoreText || "\u0415\u0449\u0451".concat(NNBSP).concat(numberOfTagsHidden);
      }
      return hideText || 'Скрыть';
    })();
    return /*#__PURE__*/_jsx("div", {
      className: styles['cloud_mode_controls'],
      ref: isFolded ? moreButtonRef : lessButtonRef,
      onClick: cloudModeControlHandler,
      children: /*#__PURE__*/_jsx(ActionLink, {
        fontSize: "S",
        fontWeight: "bold",
        children: buttonText
      })
    });
  }, [moreButtonRef, lessButtonRef, isFolded, isFoldable, hideText, showMoreText, cloudModeControlHandler, isCloudMode, numberOfTagsHidden, numberOfTagsToDisplay, isTagsFitMaxLines, expandCollapseAdornment]);
  if (!options.length) {
    return null;
  }
  const content = /*#__PURE__*/_jsx("div", {
    className: styles['tags'],
    children: /*#__PURE__*/_jsxs("div", {
      className: clsx(styles['tags_wrapper'], isCloudMode && styles['tags_cloud'], !isFolded && styles['expanded'], isPhoneDeviceType && styles['mobile']),
      ref: wrapperRef,
      children: [leftAdornment && /*#__PURE__*/_jsx("div", {
        ref: leftAdornmentRef,
        className: styles['left_adornment'],
        children: leftAdornment
      }), options.slice(0, numberOfTagsToDisplay).map(option => {
        var _option$dataTestId;
        return /*#__PURE__*/_jsx("div", {
          className: styles['tags_item'],
          id: "tag-".concat(option.value),
          children: /*#__PURE__*/_jsx(Tag, {
            onClose: () => handleClose(option),
            onClick: option.onClick ? () => {
              var _option$onClick;
              return (_option$onClick = option.onClick) === null || _option$onClick === void 0 ? void 0 : _option$onClick.call(option, option);
            } : undefined,
            icon: option.icon,
            colorType: option.colorType,
            disabled: option.disabled,
            dataTestId: (_option$dataTestId = option.dataTestId) !== null && _option$dataTestId !== void 0 ? _option$dataTestId : "tag-".concat(option.value),
            setTagWidth: setTagWidth,
            id: option.value,
            maxWidth: option.maxWidth,
            children: option.label
          })
        }, option.value);
      }), cloudModeControls, rightAdornment && /*#__PURE__*/_jsx("div", {
        ref: rightAdornmentRef,
        className: styles['right_adornment'],
        children: rightAdornment
      })]
    })
  });
  if (isCloudMode || isPhoneDeviceType) {
    return content;
  }
  return /*#__PURE__*/_jsx(Carousel, {
    ref: wrapperRef,
    children: content
  });
};
Tags.displayName = 'Tags';